/**
 * PCI-compliant Stripe billing integration.
 *
 * Card data (PAN, CVV, expiry) is NEVER handled by this application.
 * Stripe Elements collects card data in a hosted iframe and returns
 * a PaymentMethod token. Only that token is sent to your backend.
 *
 * Setup requirements:
 *   npm install @stripe/stripe-js
 *
 * In your component, load Stripe once at the app root:
 *   import { loadStripe } from '@stripe/stripe-js';
 *   const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
 *
 * Then wrap your form with <Elements stripe={stripePromise}> from '@stripe/react-stripe-js'
 * and use the <CardElement> component for card input.
 */

import type { Stripe, StripeElements } from '@stripe/stripe-js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StripeBillingInput = {
  email: string;
  billingName: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  // ✅ No card fields here — card data never enters application code
};

/** Payload sent to your Lambda. Contains only a Stripe token, never raw card data. */
type StripeBillingPayload = {
  tenantId: string;
  paymentMethodId: string; // Stripe-issued token, safe to transmit
  billingName: string;
  billingAddress: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  };
};

/** Payload to create a Stripe customer without attaching a payment method yet. */
type CreateCustomerPayload = {
  tenantId: string;
  billingName: string;
  billingAddress: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  };
};

export type CreateStripeCustomerResult = {
  stripeCustomerID: string;
  stripeStatus: string;
  card: {
    id: string;
    nickname: string;
  };
  raw: unknown;
};

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const STRIPE_BILLING_API_URL =
  process.env.REACT_APP_STRIPE_BILLING_API_URL ||
  (() => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[stripe] REACT_APP_STRIPE_BILLING_API_URL is not set. Requests will fail.');
    }
    return '';
  })();

const SETUP_INTENT_API_URL =
  process.env.REACT_APP_STRIPE_SETUP_INTENT_URL ||
  (() => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[stripe] REACT_APP_STRIPE_SETUP_INTENT_URL is not set. Requests will fail.');
    }
    return '';
  })();

// ---------------------------------------------------------------------------
// Step 1 — Tokenise card data via Stripe.js (runs in the browser)
// ---------------------------------------------------------------------------

/**
 * Creates a Stripe PaymentMethod using card data collected by Stripe Elements.
 * Card data is sent directly to Stripe's servers from the hosted iframe —
 * it never passes through your application code or network.
 *
 * @param stripe  - Stripe instance from useStripe() hook
 * @param elements - StripeElements instance from useElements() hook
 * @param billingData - Billing details (no card fields)
 * @returns Stripe PaymentMethod ID (e.g. "pm_xxx")
 */
export async function tokeniseCard(
  stripe: Stripe,
  elements: StripeElements,
  billingData: StripeBillingInput
): Promise<string> {
  const cardElement = elements.getElement('card');

  if (!cardElement) {
    throw new Error(
      'Stripe CardElement not found. Ensure <CardElement> is mounted before submitting.'
    );
  }

  const { paymentMethod, error } = await stripe.createPaymentMethod({
    type: 'card',
    card: cardElement,
    billing_details: {
      name: billingData.billingName,
      email: billingData.email,
      address: {
        line1: billingData.billingAddressLine1,
        line2: billingData.billingAddressLine2 || '',
        city: billingData.billingCity,
        state: billingData.billingState,
        postal_code: billingData.billingZip,
        country: 'US',
      },
    },
  });

  if (error || !paymentMethod) {
    throw new Error(error?.message || 'Failed to create Stripe PaymentMethod');
  }

  return paymentMethod.id;
}

// ---------------------------------------------------------------------------
// Step 2 — Build the payload for your Lambda (token only, no card data)
// ---------------------------------------------------------------------------

export function buildStripeBillingPayload(
  billingData: StripeBillingInput,
  paymentMethodId: string
): StripeBillingPayload {
  return {
    tenantId: billingData.email,
    paymentMethodId, // ✅ Token — safe to log, transmit, and store
    billingName: billingData.billingName,
    billingAddress: {
      addressLine1: billingData.billingAddressLine1,
      addressLine2: billingData.billingAddressLine2 || '',
      city: billingData.billingCity,
      state: billingData.billingState,
      zip: billingData.billingZip,
    },
  };
}

// ---------------------------------------------------------------------------
// Step 2b — Create a Stripe customer with no payment method attached yet
//           (use this when switching to the SetupIntent flow)
// ---------------------------------------------------------------------------

/**
 * Creates a Stripe customer record on your backend without attaching a card.
 * Call this before createSetupIntent so you have a customerId to pass.
 *
 * Your Lambda should call stripe.customers.create({ email, name }) and return
 * { stripeCustomerID: customer.id } (same shape as the existing Lambda).
 */
export async function createStripeCustomerOnly(
  billingData: StripeBillingInput
): Promise<string> {
  const payload: CreateCustomerPayload = {
    tenantId: billingData.email,
    billingName: billingData.billingName,
    billingAddress: {
      addressLine1: billingData.billingAddressLine1,
      addressLine2: billingData.billingAddressLine2 || '',
      city: billingData.billingCity,
      state: billingData.billingState,
      zip: billingData.billingZip,
    },
  };

  const response = await fetch(STRIPE_BILLING_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const rawText = await response.text();
  let parsed: any;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    throw new Error(`Customer creation returned non-JSON: ${rawText}`);
  }

  if (!response.ok) {
    throw new Error(`Customer creation failed (${response.status}): ${rawText}`);
  }

  const body =
    parsed?.body && typeof parsed.body === 'string'
      ? JSON.parse(parsed.body)
      : parsed;

  const customerId = body?.stripeCustomerID || body?.customerId;
  if (!customerId) {
    throw new Error('Customer creation response missing stripeCustomerID/customerId');
  }

  return customerId;
}

// ---------------------------------------------------------------------------
// Step 3 — Send token to your Lambda and create the Stripe customer
// ---------------------------------------------------------------------------

/**
 * Sends the tokenised billing payload to your Lambda and returns the
 * resulting Stripe customer details.
 *
 * Your Lambda should use the paymentMethodId to call the Stripe API
 * server-side (e.g. stripe.customers.create + stripe.paymentMethods.attach).
 */
export async function createStripeCustomer(
  billingData: StripeBillingInput,
  paymentMethodId: string
): Promise<CreateStripeCustomerResult> {
  const payload = buildStripeBillingPayload(billingData, paymentMethodId);

  const response = await fetch(STRIPE_BILLING_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const rawResponseText = await response.text();
  let parsedResponse: any = null;

  if (rawResponseText) {
    try {
      parsedResponse = JSON.parse(rawResponseText);
    } catch {
      throw new Error(`Stripe billing Lambda returned non-JSON response: ${rawResponseText}`);
    }
  }

  if (!response.ok) {
    throw new Error(
      `Stripe billing Lambda request failed (${response.status}): ${rawResponseText || 'No response body'}`
    );
  }

  const lambdaPayload =
    parsedResponse?.body && typeof parsedResponse.body === 'string'
      ? JSON.parse(parsedResponse.body)
      : parsedResponse;

  const stripeCustomerID =
    lambdaPayload?.stripeCustomerID || lambdaPayload?.customerId;

  if (!stripeCustomerID) {
    throw new Error('Stripe billing Lambda response missing stripeCustomerID/customerId');
  }

  const stripeStatus =
    lambdaPayload?.stripeStatus || lambdaPayload?.status || 'incomplete';

  const card = lambdaPayload?.card || {
    id: lambdaPayload?.paymentMethodId || paymentMethodId,
    nickname: lambdaPayload?.cardNickname || 'Card on file',
  };

  return { stripeCustomerID, stripeStatus, card, raw: lambdaPayload };
}

// ---------------------------------------------------------------------------
// SetupIntent flow (recommended — handles 3D Secure automatically)
// ---------------------------------------------------------------------------

/**
 * Asks your Lambda to create a Stripe SetupIntent for the given customer and
 * returns its client_secret.
 *
 * Your Lambda should call:
 *   stripe.setupIntents.create({ customer: customerId, usage: 'off_session' })
 * and return { clientSecret: setupIntent.client_secret }.
 */
export async function createSetupIntent(customerId: string): Promise<string> {
  const response = await fetch(SETUP_INTENT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customerId }),
  });

  const rawText = await response.text();
  let data: any;
  try {
    data = JSON.parse(rawText);
  } catch {
    throw new Error(`create-setup-intent returned non-JSON: ${rawText}`);
  }

  if (!response.ok) {
    throw new Error(`create-setup-intent failed (${response.status}): ${rawText}`);
  }

  const clientSecret = data.clientSecret ?? data.client_secret;
  if (!clientSecret) {
    throw new Error('create-setup-intent response missing clientSecret');
  }

  return clientSecret;
}

/**
 * Confirms a SetupIntent using the card collected by Stripe Elements.
 * Stripe handles 3DS challenges automatically inside this call.
 * Returns the confirmed PaymentMethod ID (pm_xxx).
 */
export async function confirmCardSetup(
  stripe: Stripe,
  elements: StripeElements,
  clientSecret: string,
  billingData: StripeBillingInput
): Promise<string> {
  const cardElement = elements.getElement('card');
  if (!cardElement) {
    throw new Error('Stripe CardElement not found. Ensure <CardElement> is mounted.');
  }

  const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
    payment_method: {
      card: cardElement,
      billing_details: {
        name: billingData.billingName,
        email: billingData.email,
        address: {
          line1: billingData.billingAddressLine1,
          line2: billingData.billingAddressLine2 || '',
          city: billingData.billingCity,
          state: billingData.billingState,
          postal_code: billingData.billingZip,
          country: 'US',
        },
      },
    },
  });

  if (error || !setupIntent) {
    throw new Error(error?.message || 'Card setup confirmation failed');
  }

  return setupIntent.payment_method as string;
}

// ---------------------------------------------------------------------------
// Combined helpers
// ---------------------------------------------------------------------------

export type SetupIntentResult = {
  customerId: string;
  paymentMethodId: string;
};

/**
 * Full SetupIntent flow — preferred over submitStripeBilling.
 *
 * 1. Creates a Stripe customer on your backend (no card attached yet).
 * 2. Creates a SetupIntent (usage: 'off_session') so the card can be charged
 *    later without the customer present — e.g. when they move in.
 * 3. Confirms the card via stripe.confirmCardSetup(), which handles 3DS automatically.
 *
 * The customer is NOT charged here. Charge them later server-side with:
 *   stripe.paymentIntents.create({ customer: customerId, payment_method: pmId, confirm: true, off_session: true })
 */
export async function submitStripeBillingViaSetupIntent(
  stripe: Stripe,
  elements: StripeElements,
  billingData: StripeBillingInput
): Promise<SetupIntentResult> {
  const customerId = await createStripeCustomerOnly(billingData);
  const clientSecret = await createSetupIntent(customerId);
  const paymentMethodId = await confirmCardSetup(stripe, elements, clientSecret, billingData);
  return { customerId, paymentMethodId };
}

/**
 * Legacy PaymentMethod-based flow. Kept for backwards compatibility.
 * Prefer submitStripeBillingViaSetupIntent for new code.
 */
export async function submitStripeBilling(
  stripe: Stripe,
  elements: StripeElements,
  billingData: StripeBillingInput
): Promise<CreateStripeCustomerResult> {
  // 1. Tokenise — card data goes to Stripe, we get back a safe token
  const paymentMethodId = await tokeniseCard(stripe, elements, billingData);

  // 2. Create customer on your backend using only the token
  return createStripeCustomer(billingData, paymentMethodId);
}